import type { ServiceData } from '../../data/services';
import { useConsultaModal } from '../../context/ConsultaModalContext';

interface ServiceContentProps {
    data: ServiceData;
}

export const ServiceContent = ({ data }: ServiceContentProps) => {
    const { open: openConsultaModal } = useConsultaModal();
    return (
        <div className="lg:col-span-2 space-y-24 font-vanguard-sans mb-12">
            {/* Visão Geral (Overview) */}
            {(data.overview.title || data.overview.content.length > 0) && (
                <section>
                    {data.overview.title && (
                        <div className="flex items-center gap-4 mb-10">
                            <span className="w-8 h-[1px] bg-vanguard-primary"></span>
                            <h2 className="text-3xl font-bold text-stone-900 dark:text-white uppercase tracking-tight font-serif">
                                {data.overview.title}
                            </h2>
                        </div>
                    )}
                    <div className="prose prose-lg dark:prose-invert max-w-none text-stone-600 dark:text-stone-400 space-y-8 leading-relaxed font-light text-justify">
                        {data.overview.content.map((paragraph, idx) => (
                            <p key={idx}>
                                {paragraph}
                            </p>
                        ))}
                    </div>
                </section>
            )}

            {/* Áreas Especializadas (Detailed Subtopics) */}
            {data.specializedAreas.items.length > 0 && <section className="space-y-20">
                {data.specializedAreas.title && (
                    <div className="border-b border-stone-100 dark:border-stone-800 pb-8">
                        <h2 className="text-2xl font-bold text-stone-900 dark:text-white uppercase tracking-[0.3em] font-vanguard-display">
                            {data.specializedAreas.title}
                        </h2>
                    </div>
                )}

                <div className="space-y-24">
                    {data.specializedAreas.items.map((area) => (
                        <div key={area.id} id={area.id} className="scroll-mt-32 group">
                            <div className="flex flex-col md:flex-row gap-10">
                                {/* Icon Column — only shown when there is content */}
                                {area.detailedDescription.length > 0 && (
                                    <div className="flex-shrink-0">
                                        <div className="w-16 h-16 border border-stone-200 dark:border-stone-800 flex items-center justify-center text-vanguard-primary transition-colors group-hover:border-vanguard-primary/50">
                                            <span className="material-symbols-outlined text-3xl">{area.icon}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Content Column */}
                                <div className="space-y-8">
                                    {area.detailedDescription.length > 0 && (
                                        <>
                                            <h3 className="text-2xl font-bold text-stone-900 dark:text-white uppercase tracking-wide font-vanguard-display group-hover:text-vanguard-primary transition-colors">
                                                {area.title}
                                            </h3>

                                            <div className="space-y-6 text-stone-500 dark:text-stone-400 font-light leading-relaxed text-lg text-justify">
                                                {area.detailedDescription.map((p, pIdx) => (
                                                    <p key={pIdx}>{p}</p>
                                                ))}
                                            </div>
                                        </>
                                    )}

                                    {/* Imagem destacada opcional */}
                                    {area.image && (
                                        <div className="w-full h-72 overflow-hidden border border-stone-100 dark:border-stone-800">
                                            <img
                                                src={area.image}
                                                alt={area.title}
                                                className="w-full h-full object-cover grayscale-[0.2] hover:grayscale-0 transition-all duration-700"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}

                                    {/* Benefits List — grouped or flat */}
                                    {area.benefitGroups && area.benefitGroups.length > 0 ? (
                                        <div className="space-y-0 mt-8">
                                            {area.benefitGroups.map((group, gIdx) => {
                                                if (group.ordered) {
                                                    return (
                                                        <div key={gIdx} className="bg-stone-50 dark:bg-stone-900/30 p-8 border-l-2 border-vanguard-primary shadow-sm">
                                                            {group.label && (
                                                                <h4 className="text-[12px] font-bold text-stone-400 uppercase tracking-[0.3em] mb-6">{group.label}</h4>
                                                            )}
                                                            <ol className="space-y-3 list-decimal list-inside">
                                                                {group.items.map((item, iIdx) => (
                                                                    <li key={iIdx} className="text-base font-medium text-stone-700 dark:text-stone-300 leading-relaxed">
                                                                        {item}
                                                                    </li>
                                                                ))}
                                                            </ol>
                                                        </div>
                                                    );
                                                }
                                                if (group.alphabetical) {
                                                    return (
                                                        <div key={gIdx} className="bg-stone-50 dark:bg-stone-900/30 px-8 pb-8 border-l-2 border-vanguard-primary shadow-sm -mt-px">
                                                            <ol className="space-y-3 list-[lower-alpha] list-inside pl-6">
                                                                {group.items.map((item, iIdx) => (
                                                                    <li key={iIdx} className="text-base font-medium text-stone-700 dark:text-stone-300 leading-relaxed">
                                                                        {item}
                                                                    </li>
                                                                ))}
                                                            </ol>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div key={gIdx} className="bg-stone-50 dark:bg-stone-900/30 p-8 border-l-2 border-vanguard-primary shadow-sm mt-6">
                                                        <div className="flex flex-wrap items-baseline gap-3 mb-6">
                                                            <h4 className="text-[12px] font-bold text-stone-400 uppercase tracking-[0.3em]">{group.label}</h4>
                                                            {group.note && (
                                                                <span className="text-[12px] text-vanguard-primary font-semibold uppercase tracking-[0.15em]">— {group.note}</span>
                                                            )}
                                                        </div>
                                                        <ul className={`grid gap-y-4 gap-x-10 ${group.singleColumn ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                                                            {group.items.map((item, iIdx) => (
                                                                <li key={iIdx} className="flex items-start gap-3">
                                                                    <span className="material-icons text-vanguard-primary text-sm mt-0.5">check</span>
                                                                    <span className="text-base font-medium text-stone-700 dark:text-stone-300">{item}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : area.benefits && area.benefits.length > 0 ? (
                                        <ol className="mt-8 space-y-5">
                                            {area.benefits.map((benefit, bIdx) => (
                                                <li key={bIdx} className="flex items-start gap-4">
                                                    <span className="flex-shrink-0 w-7 h-7 rounded-full border border-vanguard-primary text-vanguard-primary text-xs font-bold flex items-center justify-center mt-0.5">
                                                        {bIdx + 1}
                                                    </span>
                                                    <span className="text-base text-stone-600 dark:text-stone-400 font-light leading-relaxed">{benefit}</span>
                                                </li>
                                            ))}
                                        </ol>
                                    ) : null}

                                    {/* Parágrafos após listas */}
                                    {area.postListDescription && area.postListDescription.length > 0 && (
                                        <div className="space-y-6 text-stone-500 dark:text-stone-400 font-light leading-relaxed text-lg text-justify mt-8">
                                            {area.postListDescription.map((p, pIdx) => (
                                                <p key={pIdx}>{p}</p>
                                            ))}
                                        </div>
                                    )}

                                    {/* FAQ da área */}
                                    {area.faq && area.faq.length > 0 && (
                                        <div className="space-y-6 mt-8 border-t border-stone-100 dark:border-stone-800 pt-8">
                                            <h4 className="text-[12px] font-bold text-stone-400 uppercase tracking-[0.3em]">Dúvidas Frequentes</h4>
                                            <div className="space-y-6">
                                                {area.faq.map((item, fIdx) => (
                                                    <div key={fIdx} className="border-b border-stone-100 dark:border-stone-800 pb-6 last:border-0">
                                                        <h5 className="text-base font-bold text-stone-900 dark:text-white mb-2 font-vanguard-display">
                                                            {item.question}
                                                        </h5>
                                                        <p className="text-base text-stone-500 dark:text-stone-400 font-light leading-relaxed">
                                                            {item.answer}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>}


            {/* CTA opcional */}
            {data.cta && (
                <section className="border-t border-stone-100 dark:border-stone-800 pt-16">
                    <div className="bg-stone-50 dark:bg-stone-900/30 p-12 flex flex-col md:flex-row items-center justify-between gap-8">
                        <p className="text-xl text-stone-700 dark:text-stone-300 font-light leading-relaxed max-w-lg">
                            {data.cta.text}
                        </p>
                        <button
                            onClick={() => openConsultaModal(data.specializedAreas.title)}
                            className="flex-shrink-0 bg-pacces-ebony text-white font-bold px-10 py-4 text-[12px] uppercase tracking-[0.3em] hover:bg-pacces-gold hover:text-pacces-ebony transition-all duration-300 flex items-center gap-3"
                        >
                            {data.cta.buttonLabel}
                            <span className="material-icons text-sm">arrow_forward</span>
                        </button>
                    </div>
                </section>
            )}

            {/* Estudos de Caso (Success Cases) — ocultado quando vazio */}
            {data.successCases.items.length > 0 && (
                <section>
                    <div className="flex items-end justify-between mb-12 border-b border-stone-100 dark:border-stone-800 pb-6">
                        <h2 className="text-3xl font-bold text-stone-900 dark:text-white uppercase tracking-tight font-serif">
                            Resultados Estratégicos
                        </h2>
                        <a className="text-stone-400 font-bold text-[12px] uppercase tracking-widest hover:text-vanguard-primary flex items-center gap-2 transition-colors border-b border-transparent hover:border-vanguard-primary" href="#">
                            Ver Portfólio <span className="material-icons text-sm">east</span>
                        </a>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {data.successCases.items.map((item, idx) => (
                            <div key={idx} className="group border border-stone-100 dark:border-stone-800 bg-white dark:bg-stone-900/50 transition-all duration-500 hover:border-vanguard-primary/50 hover:shadow-2xl">
                                <div className="h-64 overflow-hidden relative">
                                    <div className="absolute inset-0 bg-stone-900/20 group-hover:bg-transparent transition-all duration-700 z-10"></div>
                                    <img alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" src={item.image} loading="lazy" />
                                </div>
                                <div className="p-10">
                                    <span className="text-[12px] font-bold text-stone-400 uppercase tracking-[0.3em] mb-4 block">{item.tag}</span>
                                    <h4 className="text-xl font-bold text-stone-900 dark:text-white mb-4 leading-snug group-hover:text-vanguard-primary transition-colors font-vanguard-display uppercase tracking-tighter">{item.title}</h4>
                                    <p className="text-base text-stone-500 dark:text-stone-400 mb-6 leading-relaxed font-light">{item.description}</p>
                                    <button className="text-stone-900 dark:text-white font-bold text-[12px] uppercase tracking-[0.2em] flex items-center gap-2 border-b border-stone-100 dark:border-stone-800 pb-2 group-hover:border-vanguard-primary transition-all">Ver Detalhes do Caso <span className="material-icons text-[12px] text-vanguard-primary">add</span></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};
